import * as React from "react";
import { Trash2 } from "lucide-react";
import type { SelectionAction } from "../../../ui/components/Table";
import * as userApi from "../../lib/api/users";

function DisableIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
    </svg>
  );
}

export interface BuildOrgSelectionActionsOptions {
  accessToken: string;
  canDisable: boolean;
  canHardDelete: boolean;
  confirm: (title: string, body: string, confirmLabel?: string) => Promise<boolean>;
  onMutationComplete?: () => void;
}

export function buildOrgSelectionActions(
  opts: BuildOrgSelectionActionsOptions,
): SelectionAction[] {
  const { accessToken, canDisable, canHardDelete, confirm, onMutationComplete } = opts;
  const actions: SelectionAction[] = [];

  if (canDisable) {
    actions.push({
      icon: <DisableIcon />,
      label: "Disable",
      danger: true,
      onClick: async (ids, names) => {
        const title = ids.length === 1 && names?.[0]
          ? `Disable ${names[0]}?`
          : `Disable ${ids.length} organization${ids.length !== 1 ? 's' : ''}?`
        const ok = await confirm(
          title,
          "Members will lose access until the organization is restored.",
          "Disable",
        );
        if (!ok) return;
        try {
          await Promise.all(ids.map((id) => userApi.disableOrganization(accessToken, id)));
          onMutationComplete?.();
        } catch (err) {
          window.alert(err instanceof Error ? err.message : "Disable failed");
        }
      },
    });
  }

  if (canHardDelete) {
    actions.push({
      icon: <Trash2 size={14} />,
      label: "Delete permanently",
      danger: true,
      onClick: async (ids, names) => {
        const title = ids.length === 1 && names?.[0]
          ? `Delete ${names[0]} permanently?`
          : `Delete ${ids.length} organization${ids.length !== 1 ? 's' : ''} permanently?`
        const ok = await confirm(
          title,
          "This will permanently remove the selected organizations and all their data. This cannot be undone.",
          "Delete permanently",
        );
        if (!ok) return;
        try {
          await Promise.all(
            ids.map((id) => userApi.deleteOrganizationPermanently(accessToken, id)),
          );
          onMutationComplete?.();
        } catch (err) {
          window.alert(err instanceof Error ? err.message : "Delete failed");
        }
      },
    });
  }

  return actions;
}
